import React, { useEffect, useState } from 'react';
import { TrendingUp, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => {
  const token = localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const ELIGIBLE_TYPES = new Set(['quoted_stocks', 'etf', 'mutual_fund', 'private_equity']);

function fmt(v, cur) {
  if (v == null) return '—';
  try {
    return `${cur ? cur + ' ' : ''}${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  } catch {
    return `${v}`;
  }
}

/**
 * "Income Summary" mini-card — Phase C of P1.2.
 *
 * Aggregates ONLY dividends / distributions on the current holding (lifetime).
 * Bond coupons and other derived income are intentionally excluded so this card
 * stays scoped to user-recorded receipts.
 *
 * Renders only for dividend-eligible investment types.
 */
export default function IncomeSummaryCard({ investment }) {
  const eligible = investment && ELIGIBLE_TYPES.has(investment.type);
  const investmentId = investment?.investment_id;
  const currency = investment?.currency || '';
  const originalCost = Number(investment?.amount || 0);

  const [loading, setLoading] = useState(false);
  const [totals, setTotals] = useState({ gross: 0, wht: 0, count: 0 });

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!eligible || !investmentId) return;
      setLoading(true);
      try {
        const resp = await fetch(`${BACKEND_URL}/api/investments/${investmentId}/dividends`, { headers: authHeaders() });
        if (!resp.ok) return;
        const data = await resp.json();
        const events = (data.events || []).filter((e) => e.event_type === 'dividend' || e.event_type === 'distribution');
        const gross = events.reduce((acc, e) => acc + Number(e.gross_amount || 0), 0);
        const wht = events.reduce((acc, e) => acc + Number(e.withholding_tax || 0), 0);
        if (!cancelled) setTotals({ gross, wht, count: events.length });
      } catch {
        /* silent — card simply renders empty */
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [eligible, investmentId]);

  if (!eligible) return null;

  const netGross = totals.gross - totals.wht;
  const yieldPct = originalCost > 0 ? (netGross / originalCost) * 100 : null;

  return (
    <div
      className="rounded-lg border border-emerald-200 bg-linear-to-br from-emerald-50/70 to-white px-4 py-3"
      data-testid="income-summary-card"
    >
      <div className="flex items-center gap-2 mb-2">
        <TrendingUp className="w-4 h-4 text-emerald-700" />
        <span className="text-sm font-semibold text-slate-800">Income Summary (lifetime)</span>
        {loading && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400 ml-1" />}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
        <div data-testid="income-summary-count">
          <div className="text-slate-500">Events</div>
          <div className="text-slate-900 font-semibold mt-0.5">{totals.count}</div>
        </div>
        <div data-testid="income-summary-gross">
          <div className="text-slate-500">Gross</div>
          <div className="text-slate-900 font-semibold mt-0.5">{fmt(totals.gross, currency)}</div>
        </div>
        <div data-testid="income-summary-wht">
          <div className="text-slate-500">WHT</div>
          <div className="text-slate-900 font-semibold mt-0.5">{fmt(totals.wht, currency)}</div>
        </div>
        <div data-testid="income-summary-yield">
          <div className="text-slate-500">Yield vs cost</div>
          <div className="text-slate-900 font-semibold mt-0.5">
            {yieldPct == null ? '—' : `${yieldPct.toFixed(2)}%`}
          </div>
        </div>
      </div>
      <p className="text-[11px] text-slate-500 mt-2 italic">
        Lifetime totals from recorded dividends and distributions on this holding only — excludes bond coupons and other derived income.
      </p>
    </div>
  );
}
